import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type UseInfiniteQueryResult,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import type {
  NotificationPageDto,
  NotificationSummaryDto,
  NotificationUnreadCountDto,
} from '@/api/notifications-client';
import { useNotificationsClient } from '@/api/notifications-client-context';

// =============================================================================
// use-notifications.ts — Hooks TanStack Query du CENTRE DE NOTIFICATIONS (R15-COM-06).
// =============================================================================
// Le client effectif vient du NotificationsClientProvider (doublable). ZÉRO règle métier ici : l'autorité
// (RLS/session, tri récence, keyset, exclusion chat du volet G-4, recalcul badge) est l'API.
//
// Clés de cache PARTAGÉES (DEC-31 : le signal socket invalide summary+list ; les mutations aussi) :
//   • ['notifications','summary'] — cloche + widget D-04 (MÊME cache, mono-source `summary.latest`).
//   • ['notifications','list']    — volet consolidé (infinite/keyset).
// La MUTATION de lecture invalide les DEUX (badge + volet re-fetch REST, autorité serveur).

/** Racine de clé partagée — exportée pour l'invalidation par le hook temps réel (socket signal). */
export const NOTIFICATIONS_QUERY_ROOT = ['notifications'] as const;
export const NOTIFICATIONS_SUMMARY_KEY = ['notifications', 'summary'] as const;
export const NOTIFICATIONS_LIST_KEY = ['notifications', 'list'] as const;

/** Résumé (badge + 3 derniers non-lus). Sert la cloche ET le widget D-04 depuis le MÊME cache. */
export function useNotificationSummary(): UseQueryResult<
  NotificationSummaryDto,
  Error
> {
  const client = useNotificationsClient();
  return useQuery<NotificationSummaryDto, Error>({
    queryKey: NOTIFICATIONS_SUMMARY_KEY,
    queryFn: () => client.getSummary(),
  });
}

/** Volet consolidé paginé (keyset). Curseur opaque porté par `nextCursor` (autorité API). */
export function useNotificationList(): UseInfiniteQueryResult<
  InfiniteData<NotificationPageDto>,
  Error
> {
  const client = useNotificationsClient();
  return useInfiniteQuery<
    NotificationPageDto,
    Error,
    InfiniteData<NotificationPageDto>,
    typeof NOTIFICATIONS_LIST_KEY,
    string | undefined
  >({
    queryKey: NOTIFICATIONS_LIST_KEY,
    queryFn: ({ pageParam }) => client.listPage(pageParam),
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });
}

/**
 * Marque un LOT de notifications lues (auto-read à l'affichage du volet, DBU l.3252 / D-2). À la réussite :
 * invalide summary + list (badge recalculé serveur + volet re-fetch REST). Le variable = liste d'ids
 * AFFICHÉS non-lus. DBU D-1 : plus de « tout marquer lu » ; DBU D-2 : plus de marquage lu au clic.
 */
export function useMarkNotificationsRead(): UseMutationResult<
  NotificationUnreadCountDto,
  Error,
  readonly string[]
> {
  const client = useNotificationsClient();
  const queryClient = useQueryClient();
  return useMutation<NotificationUnreadCountDto, Error, readonly string[]>({
    mutationFn: (ids: readonly string[]) => client.markRead(ids),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_ROOT });
    },
  });
}
